import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase/client';
import AppointmentScheduler from '../TherapistCalendar/AppointmentScheduler';
import ModalShell from '../ui/ModalShell';
import '../TherapistCalendar/TherapistCalendar.css';
import './InvestorCalendar.css';

interface InvestorAppointment {
  id: string;
  fecha_hora: string;
  duracion: number;
  modalidad: string;
  estado: string;
  tipo_consulta: string;
  paciente_id: string;
  nombre_paciente: string;
  terapeuta_id: string;
  nombre_terapeuta: string;
  date: string;
  start_time: string;
}

interface InvestorCalendarProps {
  investor: { id: string };
}

type CalendarView = 'day' | 'week';
type MainView    = 'agenda' | 'historial';

const ROW_H       = 120;
const PPM         = ROW_H / 60;
const START_HOUR  = 9;
const END_HOUR    = 20;
const HOURS       = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i);
const GRID_HEIGHT = (END_HOUR - START_HOUR) * ROW_H;

const toLocalDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const getTherapyColor = (type: string) => {
  const t = (type ?? '').toLowerCase();
  if (t.includes('individual')) return '#1A6FBF';
  if (t.includes('pareja'))     return '#C05E00';
  if (t.includes('famil'))      return '#057A55';
  if (t.includes('sexo'))       return '#6B21A8';
  return '#1A6FBF';
};

const ESTADO_LABEL: Record<string, string> = {
  programada:  'Programada',
  confirmada:  'Confirmada',
  en_progreso: 'En progreso',
  completada:  'Completada',
  cancelada:   'Cancelada',
  no_asistio:  'No asistió',
};

// Se usan concatenados como `${estColor}18` para tintar fondos (ver
// renderHistoryCard) — deben ser hex literal, no var(). 'confirmada' y
// 'en_progreso' == var(--apple-blue) exacto (mismo criterio que
// InvestorPatientDetail.css). El resto son variantes AA-safe (texto
// legible sobre fondo claro) documentadas en InvestorConsultas.css/
// InvestorRevenue.css — 'cancelada' == --ical-danger-text.
const ESTADO_COLOR: Record<string, string> = {
  programada:  '#C05E00',
  confirmada:  '#007AFF',
  en_progreso: '#007AFF',
  completada:  '#057A55',
  cancelada:   '#CF1322',
  no_asistio:  '#6B21A8',
};

const TIPO_LABEL: Record<string, string> = {
  individual: 'Individual',
  pareja:     'Pareja',
  familiar:   'Familiar',
};

// text: '#0071e3' == var(--apple-action-blue) exacto; el verde usa
// var(--success-text) — verde AA-safe para texto en Contexto B (UI-010).
// El resto son variantes AA-safe documentadas en CLAUDE.md (WCAG tipo de terapia).
const AVATAR_COLORS = [
  { bg: 'rgba(0, 122, 255, 0.10)',  text: 'var(--apple-action-blue)' },
  { bg: 'rgba(52, 199, 89, 0.10)',  text: 'var(--success-text, #057A55)' },
  { bg: 'rgba(255, 149, 0, 0.10)',  text: '#C05E00' },
  { bg: 'rgba(107, 33, 168, 0.10)', text: '#6B21A8' },
  { bg: 'rgba(5, 122, 85, 0.10)',   text: '#057A55' },
];

const avatarColor = (id: string) => AVATAR_COLORS[id.charCodeAt(0) % AVATAR_COLORS.length];

const getInitials = (name: string) => {
  const parts = name.trim().split(' ');
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
};

const getEventPosition = (startTime: string, durationMin: number) => {
  const [h, m] = startTime.split(':').map(Number);
  return {
    top:    (h * 60 + m - START_HOUR * 60) * PPM,
    height: Math.max(durationMin * PPM, 24),
  };
};

// top del ancla de scroll = 2h antes de la hora actual, clampeado a la jornada visible
const getScrollAnchorTop = () => {
  const now = new Date();
  const currentHour = now.getHours() + now.getMinutes() / 60;
  const targetHour = Math.max(START_HOUR, Math.min(currentHour - 2, END_HOUR));
  return (targetHour - START_HOUR) * ROW_H;
};

const getWeekDays = (date: Date) => {
  const start = new Date(date);
  const day   = start.getDay();
  start.setDate(start.getDate() - day + (day === 0 ? -6 : 1));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const mapAppointment = (r: any): InvestorAppointment => {
  const dt = new Date(r.fecha_hora);
  return {
    ...r,
    date:       toLocalDateStr(dt),
    start_time: `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`,
  };
};

const TODAY_STR = toLocalDateStr(new Date());

const InvestorCalendar: React.FC<InvestorCalendarProps> = ({ investor }) => {

  const [mainView, setMainView] = useState<MainView>('agenda');

  const [calView,       setCalView]       = useState<CalendarView>(() => window.innerWidth < 768 ? 'day' : 'week');
  const [selectedDate,  setSelectedDate]  = useState(new Date());
  const [appointments,  setAppointments]  = useState<InvestorAppointment[]>([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedAppt,  setSelectedAppt]  = useState<InvestorAppointment | null>(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const prevWidthRef = useRef(window.innerWidth);
  // Ancla invisible en el grid horario — al entrar a un período que incluye "hoy" el
  // scroll arranca 2h antes de la hora actual, no en START_HOUR (09:00).
  const nowAnchorRef = useRef<HTMLDivElement>(null);

  const [history,     setHistory]     = useState<InvestorAppointment[]>([]);
  const [histLoading, setHistLoading] = useState(true);
  const [histDesde,   setHistDesde]   = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return toLocalDateStr(d);
  });
  const [histHasta,   setHistHasta]   = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toLocalDateStr(d);
  });

  useEffect(() => {
    const handle = () => {
      const w = window.innerWidth;
      if (w === prevWidthRef.current) return;
      prevWidthRef.current = w;
      if (w < 768) setCalView(v => v === 'week' ? 'day' : v);
    };
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const loadAgenda = async () => {
    setLoading(true);
    const days  = calView === 'week' ? getWeekDays(selectedDate) : [selectedDate];
    const desde = toLocalDateStr(days[0]);
    const hasta = toLocalDateStr(days[days.length - 1]);

    const { data, error } = await supabase.rpc('get_investor_calendar', {
      p_inversionista_id: investor.id,
      p_desde: desde,
      p_hasta: hasta,
    });

    if (error) {
      console.error('Error cargando agenda inversor:', error.code);
      setAppointments([]);
    } else {
      setAppointments((data ?? []).map(mapAppointment));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAgenda();
  }, [investor.id, selectedDate, calView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const load = async () => {
      setHistLoading(true);
      const { data, error } = await supabase.rpc('get_investor_calendar', {
        p_inversionista_id: investor.id,
        p_desde: histDesde,
        p_hasta: histHasta,
      });

      if (error) {
        console.error('Error cargando historial inversor:', error.code);
        setHistory([]);
      } else {
        const mapped = (data ?? []).map(mapAppointment);
        mapped.sort((a: InvestorAppointment, b: InvestorAppointment) =>
          new Date(b.fecha_hora).getTime() - new Date(a.fecha_hora).getTime()
        );
        setHistory(mapped);
      }
      setHistLoading(false);
    };
    load();
  }, [investor.id, histDesde, histHasta]); // eslint-disable-line react-hooks/exhaustive-deps

  const aptsForDate = (dateStr: string) =>
    appointments.filter(a => a.date === dateStr && a.estado !== 'cancelada');

  const navigate = (dir: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + (calView === 'week' ? dir * 7 : dir));
    setSelectedDate(d);
  };

  const weekDays = getWeekDays(selectedDate);

  const periodIncludesToday = calView === 'week'
    ? weekDays.some(d => toLocalDateStr(d) === TODAY_STR)
    : toLocalDateStr(selectedDate) === TODAY_STR;

  // Posiciona el scroll apenas se pinta un período que incluye hoy — instantáneo,
  // no 'smooth', porque es la posición de apertura, no una animación disparada por click.
  // 'loading' entra en las deps a propósito: mientras es true, el grid ni se
  // monta (ver `{!loading && (...)}` más abajo) — sin este dep el efecto corre
  // una vez contra el spinner y nunca vuelve a correr cuando el grid real aparece.
  useLayoutEffect(() => {
    if (loading || mainView !== 'agenda' || !periodIncludesToday) return;
    nowAnchorRef.current?.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainView, calView, selectedDate, loading]);

  const periodLabel = calView === 'week'
    ? `${weekDays[0].toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })} – ${weekDays[6].toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' })}`
    : selectedDate.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  // ── Render: calendar event ──────────────────────────────────────────────
  const renderEvent = (apt: InvestorAppointment) => {
    const { top, height } = getEventPosition(apt.start_time, apt.duracion);
    const color = getTherapyColor(apt.tipo_consulta);
    return (
      <div
        key={apt.id}
        className="cal-event ical-event"
        style={{
          top, height,
          background:  `${color}18`,
          borderLeft:  `3px solid ${color}`,
          color,
        }}
        onClick={e => { e.stopPropagation(); setSelectedAppt(apt); }}
        title={`${apt.nombre_paciente} — ${apt.nombre_terapeuta}`}
      >
        <div className="ical-event__title">
          {apt.nombre_paciente}
        </div>
        <div className="ical-event__meta">
          {apt.start_time} · {apt.nombre_terapeuta}
        </div>
      </div>
    );
  };

  // ── Render: calendar grid ───────────────────────────────────────────────
  const renderGrid = (dates: Date[]) => {
    const isWeek = dates.length > 1;
    return (
      <div className="cal-scroll-wrap">
        <div className="cal-time-grid">
          <div className="cal-time-gutter" style={{ height: GRID_HEIGHT }}>
            {HOURS.map(h => (
              <div
                key={h}
                className="cal-time-label"
                style={{
                  top: (h - START_HOUR) * ROW_H,
                  ...(h === START_HOUR ? { transform: 'none', paddingTop: 4 } : {}),
                }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
          {dates.map(day => {
            const dateStr  = toLocalDateStr(day);
            const isToday  = dateStr === TODAY_STR;
            const dayApts  = aptsForDate(dateStr);
            const colClass = isWeek
              ? `cal-day-col${isToday ? ' today' : ''}`
              : 'cal-events-area';
            return (
              <div key={dateStr} className={colClass} style={{ height: GRID_HEIGHT }}>
                {HOURS.map(h => (
                  <div key={h} className="cal-hline cal-hline--hour" style={{ top: (h - START_HOUR) * ROW_H }} />
                ))}
                {isToday && (
                  <div ref={nowAnchorRef} style={{ position: 'absolute', top: getScrollAnchorTop(), left: 0, width: 1, height: 1 }} />
                )}
                {dayApts.map(renderEvent)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Render: history card ────────────────────────────────────────────────
  const renderHistoryCard = (apt: InvestorAppointment) => {
    const color    = getTherapyColor(apt.tipo_consulta);
    const estColor = ESTADO_COLOR[apt.estado] ?? '#6B21A8';
    const av       = avatarColor(apt.paciente_id);
    const dt       = new Date(apt.fecha_hora);
    const dateLabel = dt.toLocaleDateString('es-CL', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    });

    return (
      <div
        key={apt.id}
        className="ical-history-card"
        style={{ borderLeft: `3px solid ${color}` }}
      >
        <div className="ical-history-card__avatar" style={{ background: av.bg, color: av.text }}>
          {getInitials(apt.nombre_paciente)}
        </div>

        <div className="ical-history-card__body">
          <div className="ical-history-card__name">
            {apt.nombre_paciente}
          </div>
          <div className="ical-history-card__meta">
            <span><i className="fas fa-user-md ical-icon-gap" />{apt.nombre_terapeuta}</span>
            <span><i className="fas fa-calendar ical-icon-gap" />{dateLabel} · {apt.start_time}</span>
            <span><i className="fas fa-clock ical-icon-gap" />{apt.duracion} min</span>
            <span className="ical-capitalize">
              <i className="fas fa-tag ical-icon-gap" style={{ color }} />
              {TIPO_LABEL[apt.tipo_consulta] ?? apt.tipo_consulta} · {apt.modalidad}
            </span>
          </div>
        </div>

        <div className="ical-history-card__status" style={{ background: `${estColor}18`, color: estColor }}>
          {ESTADO_LABEL[apt.estado] ?? apt.estado}
        </div>
      </div>
    );
  };

  // ── Main render ─────────────────────────────────────────────────────────
  return (
    <div className="therapist-calendar">

      {/* ── Header / Toolbar ── */}
      <div className="calendar-header">
        <div className="calendar-navigation">
          {mainView === 'agenda' ? (
            <>
              <button className="nav-btn" onClick={() => navigate(-1)}>
                <i className="fas fa-chevron-left" />
              </button>
              <button className="today-btn" onClick={() => setSelectedDate(new Date())}>
                Hoy
              </button>
              <button className="nav-btn" onClick={() => navigate(1)}>
                <i className="fas fa-chevron-right" />
              </button>
              <div className="current-period">
                <h2 className="ical-period-label">{periodLabel}</h2>
              </div>
              <div className="calendar-actions">
                <button className="action-btn" onClick={() => setShowScheduler(true)}>
                  <i className="fas fa-plus" />
                  Nueva Cita
                </button>
              </div>
            </>
          ) : (
            <div className="current-period">
              <h2>Historial de consultas</h2>
            </div>
          )}
        </div>

        <div className="calendar-controls">
          {mainView === 'agenda' && (
            <div className="view-switcher">
              {(['week', 'day'] as CalendarView[]).map(v => (
                <button
                  key={v}
                  className={`view-btn${calView === v ? ' active' : ''}`}
                  onClick={() => setCalView(v)}
                >
                  {v === 'week' ? 'Semana' : 'Día'}
                </button>
              ))}
            </div>
          )}

          <div className="view-switcher">
            <button
              className={`view-btn${mainView === 'agenda' ? ' active' : ''}`}
              onClick={() => setMainView('agenda')}
            >
              <i className="fas fa-calendar-alt ical-icon-gap-sm" />
              Agenda
            </button>
            <button
              className={`view-btn${mainView === 'historial' ? ' active' : ''}`}
              onClick={() => setMainView('historial')}
            >
              <i className="fas fa-history ical-icon-gap-sm" />
              Historial
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          AGENDA
      ═══════════════════════════════════════════ */}
      {mainView === 'agenda' && (
        <>
          {loading && (
            <div className="ical-loading-row">
              <div className="spinner" />
            </div>
          )}

          {!loading && (
            <>
              {/* Week header */}
              {calView === 'week' && (
                <div className="cal-week-header">
                  <div className="cal-week-gutter-spacer" />
                  {weekDays.map(day => {
                    const isToday = toLocalDateStr(day) === TODAY_STR;
                    return (
                      <div
                        key={day.toISOString()}
                        className={`cal-week-day-hdr${isToday ? ' today' : ''} ical-clickable`}
                        onClick={() => { setSelectedDate(day); setCalView('day'); }}
                      >
                        <span className="cal-week-day-name">
                          {day.toLocaleDateString('es-CL', { weekday: 'short' })}
                        </span>
                        <span className="cal-week-day-num">
                          {day.getDate()}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {calView === 'week' ? renderGrid(weekDays) : renderGrid([selectedDate])}

              {calView === 'day' && aptsForDate(toLocalDateStr(selectedDate)).length === 0 && (
                <div className="ical-day-empty">
                  <i className="fas fa-calendar-day ical-day-empty__icon" />
                  Sin consultas para este día.
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════
          HISTORIAL
      ═══════════════════════════════════════════ */}
      {mainView === 'historial' && (
        <div className="ical-historial">

          {/* Filtro de fechas */}
          <div className="ical-historial__filter-row">
            {!histLoading && (
              <p className="ical-historial__count">
                {history.length} consulta{history.length !== 1 ? 's' : ''} en el período
              </p>
            )}

            <div className="ical-historial__dates">
              {(['desde', 'hasta'] as const).map(field => (
                <label key={field} className="ical-historial__date-label">
                  {field === 'desde' ? 'Desde' : 'Hasta'}
                  <input
                    type="date"
                    value={field === 'desde' ? histDesde : histHasta}
                    onChange={e => field === 'desde' ? setHistDesde(e.target.value) : setHistHasta(e.target.value)}
                    className="ical-historial__date-input"
                  />
                </label>
              ))}
            </div>
          </div>

          {histLoading && (
            <div className="ical-loading-row">
              <div className="spinner" />
            </div>
          )}

          {!histLoading && history.length === 0 && (
            <div className="ical-historial__empty">
              <i className="fas fa-calendar-times ical-historial__empty-icon" />
              Sin consultas en el período seleccionado.
            </div>
          )}

          {!histLoading && history.length > 0 && (
            <div className="ical-historial__list">
              {history.map(renderHistoryCard)}
            </div>
          )}
        </div>
      )}

      {/* ── Modal detalle ── */}
      {selectedAppt && (
        <ModalShell
          title={selectedAppt.nombre_paciente}
          onClose={() => setSelectedAppt(null)}
          accentColor={getTherapyColor(selectedAppt.tipo_consulta)}
        >
              <div className="appointment-info">
                <div className="info-row">
                  <label><i className="fas fa-user-md ical-icon-gap-md" />Terapeuta</label>
                  <span>{selectedAppt.nombre_terapeuta}</span>
                </div>
                <div className="info-row">
                  <label><i className="fas fa-calendar ical-icon-gap-md" />Fecha</label>
                  <span>
                    {new Date(selectedAppt.fecha_hora).toLocaleDateString('es-CL', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                    })}
                  </span>
                </div>
                <div className="info-row">
                  <label><i className="fas fa-clock ical-icon-gap-md" />Hora</label>
                  <span>{selectedAppt.start_time} · {selectedAppt.duracion} min</span>
                </div>
                <div className="info-row">
                  <label><i className="fas fa-tag ical-icon-gap-md" />Tipo</label>
                  <span className="ical-capitalize">
                    {TIPO_LABEL[selectedAppt.tipo_consulta] ?? selectedAppt.tipo_consulta} · {selectedAppt.modalidad}
                  </span>
                </div>
                <div className="info-row">
                  <label>
                    <i
                      className="fas fa-circle ical-status-dot"
                      style={{ color: ESTADO_COLOR[selectedAppt.estado] ?? '#6B21A8' }}
                    />
                    Estado
                  </label>
                  <span className="ical-status-text" style={{ color: ESTADO_COLOR[selectedAppt.estado] ?? '#6B21A8' }}>
                    {ESTADO_LABEL[selectedAppt.estado] ?? selectedAppt.estado}
                  </span>
                </div>
              </div>
        </ModalShell>
      )}

      {showScheduler && (
        <AppointmentScheduler
          mode="investor"
          investorId={investor.id}
          onAppointmentCreated={() => { setShowScheduler(false); loadAgenda(); }}
          onCancel={() => setShowScheduler(false)}
        />
      )}
    </div>
  );
};

export default InvestorCalendar;
